// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Dobrev IT Ltd

import { useEffect, useState } from "react";
import { Dropdown, type MenuProps } from "antd";
import { MoreOutlined } from "@ant-design/icons";

// PollResult is the aggregated tally for one poll.
export interface PollResult {
  counts: number[];
  mine: number | null; // the index this user voted for, or null
  total: number; // distinct voters
}

function endLabel(expiresTs: number, closed: boolean): string {
  if (closed) return "Poll closed";
  const mins = Math.max(1, Math.round((expiresTs - Date.now()) / 60000));
  return mins >= 60 ? `Closes in ${Math.round(mins / 60)}h` : `Closes in ${mins}m`;
}

// PollCard renders a poll inline in the timeline: the question, each option as a
// clickable bar with a live count, the viewer's choice highlighted, an optional
// expiry (after which voting is disabled), and a delete action for author/ops.
export function PollCard({
  question,
  answers,
  result,
  expiresTs,
  canDelete,
  onVote,
  onDelete,
}: {
  readonly question: string;
  readonly answers: string[];
  readonly result: PollResult;
  readonly expiresTs?: number;
  readonly canDelete?: boolean;
  readonly onVote: (index: number) => void;
  readonly onDelete?: () => void;
}) {
  // Re-render exactly when the poll expires so it flips to "closed" on its own.
  const [, tick] = useState(0);
  useEffect(() => {
    if (!expiresTs) return;
    const ms = expiresTs - Date.now();
    if (ms <= 0) return;
    const t = setTimeout(() => tick((n) => n + 1), ms + 100);
    return () => clearTimeout(t);
  }, [expiresTs]);

  const closed = expiresTs !== undefined && Date.now() >= expiresTs;
  const total = result.total;
  const menu: MenuProps["items"] = [{ key: "delete", danger: true, label: "Delete poll", onClick: onDelete }];

  return (
    <div className={`poll-card ${closed ? "poll-closed" : ""}`}>
      <div className="poll-head">
        <span className="poll-question">📊 {question}</span>
        {canDelete && onDelete && (
          <Dropdown trigger={["click"]} menu={{ items: menu }} placement="bottomRight">
            <button type="button" className="poll-menu" title="Poll actions">
              <MoreOutlined />
            </button>
          </Dropdown>
        )}
      </div>
      {answers.map((answer, i) => {
        const count = result.counts[i] ?? 0;
        const pct = total > 0 ? Math.round((count / total) * 100) : 0;
        return (
          <button
            key={`${i}-${answer}`}
            type="button"
            className={`poll-option ${result.mine === i ? "mine" : ""}`}
            disabled={closed}
            onClick={() => !closed && onVote(i)}
          >
            <span className="poll-bar" style={{ width: `${pct}%` }} />
            <span className="poll-option-text">{answer}</span>
            <span className="poll-count">{count}</span>
          </button>
        );
      })}
      <div className="poll-total">
        {total} vote{total === 1 ? "" : "s"}
        {expiresTs !== undefined && <span className="poll-expiry"> · {endLabel(expiresTs, closed)}</span>}
      </div>
    </div>
  );
}
